import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Col, Row, Nav, NavItem, NavLink, TabContent, TabPane, Card } from "reactstrap";
import classnames from "classnames";
import { CheckUserPermission } from "../../AppSystem/Helpers/CheckUserPermission";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";
import { ADMIN_PATHS } from "../../config";
import AdminEconomic from "./AdminEconomic";
import AdminAccountingList from "../../Accounting/Views/AdminAccountingList";
import AdminCostBearerList from "../../CostBearer/Views/AdminCostBearerList";
import AdminVatAccountList from "../../Invoice/Views/AdminVatAccountList";
import { CheckMobile } from "../../AppSystem/Helpers/CheckMobile";

export default function AdminSystemSettings() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = CheckMobile(998);
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();

    // Map URL path to tab identifier
    const tabMap = {
        [ADMIN_PATHS.ADMIN_ECONOMIC]: "economic",
        [ADMIN_PATHS.ADMIN_ACCOUNTING_LIST]: "accounting",
        [ADMIN_PATHS.ADMIN_COST_BEARER]: "cost-bearer",
        [ADMIN_PATHS.ADMIN_INVOICE_ACCOUNTS]: "invoice-accounts"
    };

    // Determine current active tab
    const activeTab = tabMap[location.pathname] || "economic";

    // Permissions check for each tab
    const hasEconomicPermission =
        CheckUserPermission("system.setting.economic") ||
        CheckUserPermission("system.setting.economic.view") ||
        CheckUserPermission("system.setting") ||
        CheckUserPermission("system");

    const hasAccountingPermission =
        CheckUserPermission("system.setting.account-settings") ||
        CheckUserPermission("system.setting.account-settings.view") ||
        CheckUserPermission("system.setting") ||
        CheckUserPermission("system");

    const hasCostBearerPermission =
        CheckUserPermission("system.setting.cost-bearer") ||
        CheckUserPermission("system.setting.cost-bearer.view") ||
        CheckUserPermission("system.setting") ||
        CheckUserPermission("system");

    // Same permission as the former "Manage VAT" button in the invoice list
    const hasInvoiceAccountsPermission =
        CheckUserPermission("admin.invoice.manageVat") ||
        CheckUserPermission("admin.invoice") ||
        CheckUserPermission("admin");

    // Dynamic header title based on active tab
    useEffect(() => {
        if (activeTab === "economic") {
            setPageTitle(t("economic.title"));
            setPageTitleKey("economic.title");
        } else if (activeTab === "accounting") {
            setPageTitle(t("sidebar.accountingList"));
            setPageTitleKey("sidebar.accountingList");
        } else if (activeTab === "cost-bearer") {
            setPageTitle(t("costBearer.costBearer"));
            setPageTitleKey("costBearer.costBearer");
        } else if (activeTab === "invoice-accounts") {
            setPageTitle(t("invoice.invoiceAccounts"));
            setPageTitleKey("invoice.invoiceAccounts");
        }
        setOnBack(null);
        return () => {
            setPageTitle("");
            setPageTitleKey("");
            setOnBack(null);
        };
    }, [activeTab, t, setPageTitle, setPageTitleKey, setOnBack]);

    // Handle navigation when a tab is clicked
    const handleTabChange = (tabId) => {
        if (tabId === "economic" && hasEconomicPermission) {
            navigate(ADMIN_PATHS.ADMIN_ECONOMIC);
        } else if (tabId === "accounting" && hasAccountingPermission) {
            navigate(ADMIN_PATHS.ADMIN_ACCOUNTING_LIST);
        } else if (tabId === "cost-bearer" && hasCostBearerPermission) {
            navigate(ADMIN_PATHS.ADMIN_COST_BEARER);
        } else if (tabId === "invoice-accounts" && hasInvoiceAccountsPermission) {
            navigate(ADMIN_PATHS.ADMIN_INVOICE_ACCOUNTS);
        }
    };

    // Styles matching the premium look of the application
    const tabsNavStyle = {
        borderBottom: "none",
        gap: "0.5rem",
        backgroundColor: "#F7F5F2",
        borderRadius: "999px",
        padding: "0.35rem",
        // Desktop: the beige pill only wraps the tabs instead of the full card width
        ...(!isMobile ? { display: "inline-flex", alignSelf: "flex-start" } : {}),
        // Mobile: all views visible at once, each tab as wide as its label needs (no horizontal scrolling)
        ...(isMobile
            ? {
                display: "flex",
                flexWrap: "nowrap",
                gap: "0.3rem",
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                borderRadius: "14px",
                padding: "0.4rem",
            }
            : {})
    };

    const tabNavItemStyle = isMobile ? { flex: "1 1 auto", minWidth: 0 } : undefined;

    const getTabLinkStyle = (tabId) => ({
        cursor: "pointer",
        border: "none",
        borderRadius: "999px",
        padding: "10px 22px",
        color: activeTab === tabId ? "#157553" : "#5c6270",
        backgroundColor: activeTab === tabId ? "#CDE0DA" : "transparent",
        fontFamily: '"Space Grotesk", sans-serif',
        fontWeight: "600",
        fontSize: "15px",
        transition: "all 0.25s ease-in-out",
        ...(isMobile
            ? {
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                whiteSpace: "normal",
                lineHeight: 1.2,
                minHeight: "44px",
                padding: "8px 10px",
                overflowWrap: "break-word",
                borderRadius: "11px",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 700,
                fontSize: "12.5px",
                color: activeTab === tabId ? "#0B4F3A" : "#5c6270",
            }
            : {})
    });

    const tabsNav = (
        <Nav tabs style={tabsNavStyle} className="border-0">
            {hasEconomicPermission && (
                <NavItem style={tabNavItemStyle}>
                    <NavLink
                        className={classnames({ active: activeTab === "economic" }, "border-0")}
                        onClick={() => handleTabChange("economic")}
                        style={getTabLinkStyle("economic")}
                    >
                        {t("sidebar.economic")}
                    </NavLink>
                </NavItem>
            )}
            {hasAccountingPermission && (
                <NavItem style={tabNavItemStyle}>
                    <NavLink
                        className={classnames({ active: activeTab === "accounting" }, "border-0")}
                        onClick={() => handleTabChange("accounting")}
                        style={getTabLinkStyle("accounting")}
                    >
                        {t("sidebar.accountingList")}
                    </NavLink>
                </NavItem>
            )}
            {hasCostBearerPermission && (
                <NavItem style={tabNavItemStyle}>
                    <NavLink
                        className={classnames({ active: activeTab === "cost-bearer" }, "border-0")}
                        onClick={() => handleTabChange("cost-bearer")}
                        style={getTabLinkStyle("cost-bearer")}
                    >
                        {t("sidebar.costBearer")}
                    </NavLink>
                </NavItem>
            )}
            {hasInvoiceAccountsPermission && (
                <NavItem style={tabNavItemStyle}>
                    <NavLink
                        className={classnames({ active: activeTab === "invoice-accounts" }, "border-0")}
                        onClick={() => handleTabChange("invoice-accounts")}
                        style={getTabLinkStyle("invoice-accounts")}
                    >
                        {t("invoice.invoiceAccounts")}
                    </NavLink>
                </NavItem>
            )}
        </Nav>
    );

    return (
        <div
            className="page-content mx-0 px-0 mb-5"
            // Mobile: room for the fixed create button at the bottom (same as rhm-wrapper-with-create)
            style={isMobile ? { paddingBottom: "calc(90px + env(safe-area-inset-bottom, 0px))" } : undefined}
        >
            {/* Font used by the list/accordion design (loaded here so it is also available on mobile) */}
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');`}</style>
            {/* Mobile: the page title is already in the app header, so only the view switcher is shown (no card) */}
            {isMobile ? (
                <div className="mb-3">{tabsNav}</div>
            ) : (
            <Row className="mb-4">
                <Col lg={12}>
                    <Card
                        style={{
                            borderRadius: "16px",
                            border: "1px solid #E5E7EB",
                            boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                            padding: "24px 28px",
                        }}
                    >
                        <h2 className="pb-2 ps-1 mb-3 rokkitt-font" style={{ color: "#0B4F3A" }}>{t("economic.title")}</h2>
                        {tabsNav}
                    </Card>
                </Col>
            </Row>
            )}

            <TabContent activeTab={activeTab}>
                {hasEconomicPermission && (
                    <TabPane tabId="economic">
                        {activeTab === "economic" && <AdminEconomic />}
                    </TabPane>
                )}
                {hasAccountingPermission && (
                    <TabPane tabId="accounting">
                        {activeTab === "accounting" && <AdminAccountingList />}
                    </TabPane>
                )}
                {hasCostBearerPermission && (
                    <TabPane tabId="cost-bearer">
                        {activeTab === "cost-bearer" && <AdminCostBearerList />}
                    </TabPane>
                )}
                {hasInvoiceAccountsPermission && (
                    <TabPane tabId="invoice-accounts">
                        {activeTab === "invoice-accounts" && <AdminVatAccountList />}
                    </TabPane>
                )}
            </TabContent>
        </div>
    );
}
